import { MouseEvent, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { EditRequestOffer } from "./editRequest.utils";

const ROW_ACTIONS_HINT = "จัดการ";

const REQUEST_EDIT_LABEL = "ขอแก้ไข";

/** Wide enough for a refusal to wrap as a sentence rather than one word per line. */
const MENU_ITEM_MAX_WIDTH = 300;

interface EditRequestCellProps {
  /** Whether the row's ขอแก้ไข can be raised, and why not when it cannot. */
  offer: EditRequestOffer;
  onRequest: () => void;
}

/**
 * The row's overflow menu on the branch-day report, holding ขอแก้ไข.
 *
 * A kebab rather than a button in the cell: ขอแก้ไข is an occasional action on an already
 * crowded row, and a live button on every route read as something the branch was expected
 * to press.
 *
 * The kebab is on every row and opens on every row, whatever the item inside it can do. A
 * column that empties out on some rows and not others reads as a row that failed to draw,
 * which is worse than the noise it was removing.
 *
 * A blocked row therefore keeps the item, greyed, and explains itself underneath it — a
 * refusal the reader cannot work out alone (an answer already pending, the seven days up,
 * no single branch-day in view) is something they need told. The exception is the row whose
 * branch-day the branch may still edit: the item is greyed with nothing written under it,
 * because the only thing there is to say is what the reader is already doing.
 */
export default function EditRequestCell({
  offer,
  onRequest,
}: EditRequestCellProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const requestEdit = () => {
    closeMenu();
    onRequest();
  };

  const { canRequest, blockedReason } = offer;

  return (
    <TableCell align="center">
      <Tooltip title={ROW_ACTIONS_HINT}>
        <IconButton size="small" onClick={openMenu}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          disabled={!canRequest}
          onClick={requestEdit}
          sx={{ whiteSpace: "normal", maxWidth: MENU_ITEM_MAX_WIDTH }}
        >
          <Stack gap={0.25}>
            <Stack direction="row" alignItems="center" gap={1}>
              <EditNoteIcon fontSize="small" />
              <Typography variant="body2">{REQUEST_EDIT_LABEL}</Typography>
            </Stack>
            {blockedReason && (
              <Typography variant="caption" color="text.secondary">
                {blockedReason}
              </Typography>
            )}
          </Stack>
        </MenuItem>
      </Menu>
    </TableCell>
  );
}
